import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';

import { ElevatorDetailPage } from '~/components/compliance/detailPage/ElevatorDetailPage';
import { useElevatorDetail } from '~/components/compliance/detailPage/useElevatorDetail';
import { ContractDetailPage } from '~/components/contractReview/detailPage/ContractDetailPage';
import { mapContract } from '~/components/contractReview/mapContract';
import {
  CONTRACT_FORM_REVIEW_RULES_TAB,
  editingContractIdKey,
  editingContractSectionKey,
} from '~/components/global/constants';
import { InvoiceDetailPage } from '~/components/invoices/detailPage/InvoiceDetailPage';
import { TaskDetailView } from '~/components/tasks/detail/TaskDetailView';
import { HostedInWorkQueueDialogContext } from '~/components/workQueue/dialogHost';
import useContract from '~/data/hooks/useContracts';
import useInvoice from '~/data/hooks/useInvoice';
import { useTrackInvoiceOpened } from '~/hooks/useTrackInvoiceOpened';
import { BUTTON_SX } from '~/theme';

const WORK_QUEUE_DIALOG_TARGETS = ['invoice', 'contract', 'compliance', 'task'] as const;

export type WorkQueueDetailTarget = {
  type: (typeof WORK_QUEUE_DIALOG_TARGETS)[number];
  id: string;
};

export function isWorkQueueDialogTarget(type: string): type is WorkQueueDetailTarget['type'] {
  return (WORK_QUEUE_DIALOG_TARGETS as readonly string[]).includes(type);
}

/** The way out of a detail that failed to load — the pages' own back arrow is off in here. */
const BACK_LABEL = 'Back to the list';

/**
 * Sends an in-app link inside the dialog to a new tab instead of navigating under it.
 * Capture phase, so it lands before React Router's handler, which bails on an
 * already-prevented event. Everything else — modified clicks, downloads, an explicit
 * `target`, a bare `#`, other origins — keeps the behaviour it had.
 */
const openInAppLinksInNewTab = (event: React.MouseEvent<HTMLElement>): void => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const anchor = (event.target as HTMLElement | null)?.closest?.('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if (anchor.hasAttribute('download')) return;
  if (anchor.target && anchor.target !== '_self') return;

  const href = anchor.getAttribute('href') ?? '';
  if (href === '' || href.startsWith('#')) return;

  const url = new URL(anchor.href, window.location.origin);
  if (url.origin !== window.location.origin) return;

  event.preventDefault();
  window.open(url.href, '_blank', 'noopener');
};

/** What the title bar calls each target — the page below it names the record itself. */
const TITLE: Record<WorkQueueDetailTarget['type'], string> = {
  invoice: 'Invoice',
  contract: 'Contract',
  compliance: 'Elevator',
  task: 'Task',
};

interface WorkQueueDetailDialogProps {
  target: WorkQueueDetailTarget | null;
  onClose: () => void;
}

/**
 * An action item's detail, opened over the work queue instead of replacing it. Each
 * record's detail is otherwise a full page, so clicking a queue row would navigate the
 * consultant off the queue they were working, losing its filters, its scroll position
 * and their place in it. This hosts the very same page components the routes render.
 *
 * Driven by props rather than a search param: `DetailParamRedirects` watches
 * `?selectedInvoiceId` and friends and turns them into the navigation this dialog
 * exists to avoid.
 */
export function WorkQueueDetailDialog({ target, onClose }: WorkQueueDetailDialogProps) {
  const theme = useTheme();
  // Phones get the whole viewport: the invoice page pins its decision bar to the bottom
  // of the screen, and inset in a floating paper that bar would sit over the page.
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // What to keep rendering while the dialog fades out, so the detail doesn't vanish the
  // moment the item is cleared. Adjusted during render rather than in an effect — the
  // closing frame is the one that must not lose it.
  const [rendered, setRendered] = useState(target);
  if (target && target !== rendered) {
    setRendered(target);
  }

  return (
    <Dialog
      open={target !== null}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      fullScreen={fullScreen}
      TransitionProps={{ onExited: () => setRendered(null) }}
      sx={
        fullScreen
          ? undefined
          : {
              '& > .MuiDialog-container > .MuiPaper-root': {
                height: 'calc(100vh - 64px)',
                width: 'calc(100vw - 96px)',
                maxWidth: 1440,
              },
            }
      }
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">{rendered ? TITLE[rendered.type] : ''}</Typography>
        <Tooltip title="Close">
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Scrolls: the pages inside size themselves to the window and let it scroll past the
          fold, so clipping here loses the bottom of anything long — a rate table, a list of
          linked tickets — with no way to reach it. */}
      <DialogContent
        onClickCapture={openInAppLinksInNewTab}
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {rendered ? (
          // Keyed on the item so nothing — an open tab, a half-filled form — is carried from
          // the last one into the next.
          <HostedInWorkQueueDialogContext.Provider value>
            <DetailContent
              key={`${rendered.type}:${rendered.id}`}
              target={rendered}
              onClose={onClose}
            />
          </HostedInWorkQueueDialogContext.Provider>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function DetailContent({
  target,
  onClose,
}: {
  target: WorkQueueDetailTarget;
  onClose: () => void;
}) {
  switch (target.type) {
    case 'invoice':
      return <InvoiceDetail invoiceId={target.id} onClose={onClose} />;
    case 'contract':
      return <ContractDetail contractId={target.id} onClose={onClose} />;
    case 'compliance':
      return <ElevatorDetail carId={target.id} onClose={onClose} />;
    case 'task':
      return <TaskDetailView taskId={target.id} variant="dialog" />;
  }
}

/** A read that failed, with the same way out the pages give it — an empty dialog says nothing about why it is empty. */
function DetailError({
  error,
  fallback,
  onClose,
}: {
  error: unknown;
  fallback: string;
  onClose: () => void;
}) {
  return (
    <Box p={3}>
      <Alert severity="error" sx={{ mb: 2 }}>
        {error instanceof Error ? error.message : fallback}
      </Alert>
      <Button onClick={onClose} sx={BUTTON_SX}>
        {BACK_LABEL}
      </Button>
    </Box>
  );
}

function InvoiceDetail({ invoiceId, onClose }: { invoiceId: string; onClose: () => void }) {
  const { invoice, error, loading } = useInvoice(invoiceId);

  // `/spending/:invoiceId` reports the open for every other entry point, but the queue
  // renders this page without the route, so its opens were invisible. Tracked on the
  // requested id rather than the loaded invoice, so a slow or failed read still counts
  // as an open — the same thing the route does.
  useTrackInvoiceOpened(invoiceId);

  if (error) {
    return (
      <DetailError error={error} fallback="This invoice could not be loaded." onClose={onClose} />
    );
  }

  // `onBack={null}`, here and in the two below: the title bar's X is the way out, and a
  // back arrow inside the page would point at a list the reader isn't on.
  return <InvoiceDetailPage invoice={invoice} isLoading={loading} onBack={null} />;
}

function ContractDetail({ contractId, onClose }: { contractId: string; onClose: () => void }) {
  const [, setSearchParams] = useSearchParams();
  const { contract: rawContract, error, isLoading } = useContract(contractId);
  const contract = useMemo(() => (rawContract ? mapContract(rawContract) : null), [rawContract]);

  // `ContractEditDialogFlow` is mounted app-wide in `_app.tsx` and asked for through
  // the URL, exactly as the contract page asks for it — so the form opens over this
  // dialog without either knowing about the other.
  const openEditForm = useCallback(
    (section?: string) => {
      setSearchParams((prev) => {
        prev.set(editingContractIdKey, contractId);
        if (section) {
          prev.set(editingContractSectionKey, section);
        } else {
          prev.delete(editingContractSectionKey);
        }
        return prev;
      });
    },
    [contractId, setSearchParams],
  );
  const onEdit = useCallback(() => openEditForm(), [openEditForm]);
  const onAddRule = useCallback(() => openEditForm(CONTRACT_FORM_REVIEW_RULES_TAB), [openEditForm]);

  if (error) {
    return (
      <DetailError error={error} fallback="This contract could not be loaded." onClose={onClose} />
    );
  }

  return (
    <ContractDetailPage
      contract={contract}
      isLoading={isLoading}
      onBack={null}
      onEdit={onEdit}
      onAddRule={onAddRule}
    />
  );
}

function ElevatorDetail({ carId, onClose }: { carId: string; onClose: () => void }) {
  const { compliance, car, carLoading, error, isLoading, refresh } = useElevatorDetail(carId);

  const onRefresh = useCallback(() => {
    void refresh();
  }, [refresh]);

  if (error) {
    return (
      <DetailError error={error} fallback="This elevator could not be loaded." onClose={onClose} />
    );
  }

  return (
    <ElevatorDetailPage
      compliance={compliance}
      car={car}
      carLoading={carLoading}
      isLoading={isLoading}
      refresh={onRefresh}
      onBack={null}
    />
  );
}
